import { ChevronRightIcon, PhoneIcon } from '@chakra-ui/icons';
import { Box, Container, Flex, Heading, Text } from '@chakra-ui/react';
import Image from 'next/image';
import RouterLink from 'next/link';
import Link from 'next/link';
import React, { RefObject, useRef, useState } from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';

import LinkCustom from '@/app/_components/link/LinkCustom';
import { ReactComponent as ArrowBackwardCircle } from '@/app/_icons/ArrowBackwardCircleOutline.svg';
import { ReactComponent as ArrowForwardCircle } from '@/app/_icons/ArrowForwardCircleOutline.svg';
import { ReactComponent as LocationIcon } from '@/app/_icons/location_white.svg';
import { useRoutesConfig } from '@/hooks/routes-config';
import { DealerItem } from '@/types/dealers/dealers.types';
import { directusImageLoader } from '@/utils/loaders';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import styles from './close-dealerships.module.scss';

export const CloseDealerships = ({ dealers }: { dealers: DealerItem[] }) => {
  const routes = useRoutesConfig();
  const dealershipsSwiperRef: RefObject<SwiperRef> = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [currentSlidesPerView, setCurrentSlidesPerView] = useState(1);

  const getDisplayPagination = () => {
    const totalPages = Math.ceil(dealers.length / currentSlidesPerView);
    const currentPage = Math.min(
      Math.ceil(
        (currentSlide + currentSlidesPerView - 1) / currentSlidesPerView,
      ),
      totalPages,
    );

    return {
      currentPage,
      totalPages,
    };
  };

  const goNext = () => {
    if (dealershipsSwiperRef.current?.swiper) {
      dealershipsSwiperRef.current.swiper.slideNext();
    }
  };

  const goPrev = () => {
    if (dealershipsSwiperRef.current?.swiper) {
      dealershipsSwiperRef.current.swiper.slidePrev();
    }
  };

  const CustomNavigation = () => (
    <div className={styles.closedealershipsNavigation}>
      <button onClick={goPrev} className={styles.closedealershipsSwiperPrev}>
        <ArrowBackwardCircle />
      </button>
      <button onClick={goNext} className={styles.closedealershipsSwiperNext}>
        <ArrowForwardCircle />
      </button>
    </div>
  );

  const { currentPage, totalPages } = getDisplayPagination();

  return (
    <Box className={styles.closeDealershipsWrapper} paddingY="100px">
      <Container
        maxW={1280}
        width="100%"
        className={styles.servicePartsHeader}
        marginBottom={{ base: 0, md: 10 }}
        paddingX={{ base: 6, xl: 0 }}
      >
        <Box
          className={styles.closeDealershipsHeader}
          display={{ base: 'block', sm: 'flex' }}
          flexDir={{ base: 'column', md: 'row' }}
          marginBottom={{ base: 16, md: 10 }}
          alignItems={{ base: 'center', md: 'start' }}
        >
          <Box className={styles.headingElement} mb={8}>
            <RouterLink
              href={`${routes.findDealer.path}`}
              passHref
              style={{ textDecoration: 'none' }}
            >
              <Heading
                as="h2"
                className={styles.titleLg}
                textAlign={{ base: 'center', sm: 'left' }}
                color="white"
              >
                Dealerships close to you
              </Heading>
            </RouterLink>
          </Box>
          <Box marginLeft={{ md: 'auto' }} className={styles.btnCTAwrap}>
            <LinkCustom
              aria-label="View All"
              href={`${routes.findDealer.path}`}
              className="btnOutlineWhite"
              moduleClass={styles.btnCTA}
            >
              View All
              <ChevronRightIcon width={4} height={4} marginLeft={3} />
            </LinkCustom>
          </Box>
        </Box>
        <Swiper
          id="close-dealerships-slider"
          className={styles.closedealershipsSlider}
          ref={dealershipsSwiperRef}
          slidesPerView={3}
          slidesPerGroup={currentSlidesPerView}
          spaceBetween={40}
          modules={[Pagination, Navigation]}
          onSlideChange={(swiper) => {
            setCurrentSlide(swiper.activeIndex + 1);
          }}
          onBreakpoint={(swiper) => {
            setCurrentSlidesPerView(swiper.params.slidesPerView as number);
          }}
          breakpoints={{
            0: { slidesPerView: 1, slidesPerGroup: 1, spaceBetween: 40 },
            641: { slidesPerView: 2, slidesPerGroup: 2, spaceBetween: 40 },
            1024: { slidesPerView: 3, slidesPerGroup: 3, spaceBetween: 40 },
          }}
        >
          {dealers.map(
            ({
              id,
              name,
              mainAddress,
              secondaryAddress,
              mainImage,
              phoneNumber,
            }) => (
              <SwiperSlide key={id}>
                <Box
                  className={styles.closeDealershipItem}
                  display="flex"
                  flexDirection="column"
                >
                  <Box className={styles.closeDealershipItemImage}>
                    <Image
                      src={`${mainImage}?key=image-386-270`}
                      loader={directusImageLoader}
                      width={400}
                      height={476}
                      objectFit="cover"
                      alt={name || 'Dealership Image'}
                    />
                  </Box>
                  <Box
                    className={styles.closeDealershipName}
                    padding={{ base: 6 }}
                    marginTop="auto"
                  >
                    <RouterLink
                      href={`${routes.dealershipDetail.path}/${name
                        .toString()
                        .replace(/ /g, '-')
                        .toLowerCase()}`}
                      passHref
                      style={{ textDecoration: 'none' }}
                    >
                      <Heading
                        as="h4"
                        className={styles.titleMd}
                        marginBottom={{ base: 4, md: 5 }}
                        color="white"
                      >
                        {name}
                      </Heading>
                    </RouterLink>
                    <Flex dir="row" gap={3} marginBottom={3}>
                      <LocationIcon
                        className={styles.locationIcon}
                        width={18}
                        height={18}
                        marginRight={1}
                        color="white"
                      />
                      <Text className={styles.dealershipContacts}>
                        {mainAddress}, {secondaryAddress}
                      </Text>
                    </Flex>
                    <Flex dir="row" alignItems="center" gap={3}>
                      <PhoneIcon
                        width="14px"
                        height="14px"
                        marginRight={1}
                        color="white"
                      />
                      <Link
                        className={styles.dealershipContacts}
                        href={`tel:${phoneNumber}`}
                        role="link"
                      >
                        {phoneNumber}
                      </Link>
                    </Flex>
                  </Box>
                </Box>
              </SwiperSlide>
            ),
          )}
        </Swiper>
        <Box className={styles.closedealershipsSwiperControls}>
          <Box className={styles.closedealershipsPagination}>
            <span className="current-page">
              {currentPage.toString().padStart(2, '0')}
            </span>
            <span className="separator">/</span>
            <span className="total-pages">
              {totalPages.toString().padStart(2, '0')}
            </span>
          </Box>
          <CustomNavigation />
        </Box>
      </Container>
    </Box>
  );
};
